import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Factory } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, LogoMark, StatusBadge, formatNumber } from '../../../components/design-system/admin';
import { NetworkBrandListItem, useNetworkApi } from '../../../common/hooks/use-network-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { countryLabel, vocabLabel } from './network-labels';

/**
 * The brands with products in the network — the ones this shop already sells
 * first, because "add the rest of a range you stock" is the easy yes.
 */
export const BrandsGrid: React.FC = () => {
    const { t, i18n } = useTranslation();
    const api = useNetworkApi();
    const [brands, setBrands] = useState<NetworkBrandListItem[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = async () => {
        setError(null);
        try {
            setBrands((await api.brands()).items);
        } catch (e) {
            setError((await readApiError(e)).message || t('NETWORK_RETAILER.ERROR'));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    if (error) {
        return (
            <div className="rounded-xl border border-destructive/30 bg-card p-4 text-sm text-destructive" data-testid="network-brands-error">
                {error}
                <Button variant="outline" size="sm" className="ms-3" onClick={load}>
                    {t('NETWORK_RETAILER.RETRY')}
                </Button>
            </div>
        );
    }

    if (!brands) {
        return (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="network-brands-loading">
                {[0, 1, 2].map((i) => (
                    <Skeleton key={i} className="h-44 rounded-xl" />
                ))}
            </div>
        );
    }

    if (!brands.length) {
        return (
            <div className="rounded-xl border bg-card" data-testid="network-brands-empty">
                <EmptyState icon={<Factory />} title={t('NETWORK_RETAILER.BROWSE.EMPTY')} description={t('NETWORK_RETAILER.BROWSE.EMPTY_HINT')} />
            </div>
        );
    }

    return (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="network-brands">
            {brands.map((b) => (
                <Link
                    key={b.slug}
                    to={`/products/network/brands/${b.slug}`}
                    className="flex flex-col rounded-xl border bg-card p-4 transition-colors hover:bg-accent/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    data-testid="network-brand-card"
                >
                    <div className="flex items-start gap-3">
                        <LogoMark src={b.logoUrl} name={b.name} className="size-10" />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{b.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                                {[countryLabel(b.country, i18n.language), b.materials.map((m) => vocabLabel(t, m)).join(', ')].filter(Boolean).join(' · ')}
                            </p>
                        </div>
                        {b.carried && <StatusBadge tone="neutral">{t('NETWORK_RETAILER.BROWSE.YOU_SELL')}</StatusBadge>}
                    </div>
                    {b.description && <p className="mt-3 line-clamp-2 text-xs text-muted-foreground">{b.description}</p>}
                    <dl className="mt-auto grid grid-cols-3 gap-2 pt-4">
                        <Fact label={t('NETWORK_RETAILER.BROWSE.COLLECTIONS')} value={formatNumber(b.collections)} />
                        <Fact label={t('NETWORK_RETAILER.BROWSE.PRODUCTS')} value={formatNumber(b.products)} />
                        <Fact label={t('NETWORK_RETAILER.BROWSE.IN_CATALOGUE')} value={formatNumber(b.inMyCatalog)} />
                    </dl>
                </Link>
            ))}
        </div>
    );
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div>
        <dt className="text-[11px] text-muted-foreground">{label}</dt>
        <dd className="text-sm font-medium tabular-nums">{value}</dd>
    </div>
);
